import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import { useSeo } from "@/hooks/useSeo";
import { stats } from "@/mocks/site";

export default function About() {
  useSeo({
    title: "关于我们 - 欲望的诱惑",
    description:
      "了解欲望的诱惑影视内容站的定位、发展历程与服务内容范围，一个专注影视资讯、剧情介绍与演员资料的资讯平台。",
    keywords: "关于我们,欲望的诱惑,影视资讯站,影视内容平台,剧情介绍",
    canonicalPath: "/about",
  });

  const services = [
    { icon: "ri-newspaper-line", title: "影视资讯速递", desc: "持续跟踪并整理最新上线的影视作品，让你第一时间掌握影视动态。" },
    { icon: "ri-book-open-line", title: "剧情深度介绍", desc: "为每一部作品整理完整的剧情梗概，帮助你快速判断是否值得一看。" },
    { icon: "ri-user-star-line", title: "演员资料档案", desc: "收录主要演员的基本资料与代表作品，方便你按演员追溯作品。" },
    { icon: "ri-bookmark-3-line", title: "专题合集精选", desc: "由编辑挑选主题，将相似题材或同主创作品打包成合集，减少选择困难。" },
  ];

  const milestones = [
    { year: "2021", text: "「欲望的诱惑」以一个影迷博客的形式起步，开始整理影视资料。" },
    { year: "2023", text: "内容体系逐渐成型，推出分类筛选与专题合集功能。" },
    { year: "2024", text: "全面改版为影视资讯门户，加入搜索与收藏功能，服务更广泛的影迷。" },
    { year: "2025", text: "持续扩充影视与演员资料库，并升级移动端阅读体验。" },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <PageHeader
          title="关于我们"
          description="一个专注影视内容资讯与深度解读的资讯门户，为热爱影像的你整理每一部值得记住的作品。"
          crumbs={[{ label: "首页", path: "/" }, { label: "关于我们" }]}
        />

        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-accent-700 uppercase mb-3">
                <span className="w-5 h-px bg-accent-500"></span>
                网站定位
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">
                让每一次追剧，都从了解开始
              </h2>
              <div className="mt-5 space-y-4 text-sm md:text-base text-foreground-700 leading-loose">
                <p>
                  「欲望的诱惑」是一个面向普通影迷、影评人与行业关注者的影视内容站。我们不生产影视，而是把散落在各处的剧情介绍、演员资料与作品资讯，重新整理成清晰、好读、可信赖的档案。
                </p>
                <p>
                  我们相信，了解一部作品的背景与故事，会让观看本身变得更有厚度。因此，从大银幕电影到长篇剧集，从纪实影像到动画世界，我们以杂志编辑的严谨与审美的眼光，为每一部收录作品建立完整的资料页。
                </p>
                <p>
                  网站持续更新最新收录作品，并推出编辑精选的专题合集。无论你是想快速确认一部剧是否合口味，还是想按演员、按题材深挖作品，都能在这里得到答案。
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {stats.map((s) => (
                  <div key={s.label} className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6">
                    <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600">{s.value}</p>
                    <p className="mt-1.5 text-xs md:text-sm text-foreground-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-14 md:mt-20">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">
              服务内容范围
            </h2>
            <p className="text-sm md:text-base text-foreground-500 text-center max-w-2xl mx-auto mb-8 md:mb-10">
              我们提供的每一项内容，都围绕“帮你更快、更准地了解影视作品”展开。
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {services.map((s) => (
                <div key={s.title} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 hover:border-primary-300 transition-colors">
                  <span className="w-12 h-12 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
                    <i className={`${s.icon} text-2xl`}></i>
                  </span>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">{s.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-14 md:mt-20 bg-background-100 border border-background-200/70 rounded-lg p-6 md:p-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-7 flex items-center gap-2">
              <i className="ri-road-map-line text-primary-500"></i>
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-background-300/70"></div>
              <div className="space-y-6">
                {milestones.map((m) => (
                  <div key={m.year} className="relative pl-8">
                    <span className="absolute left-0 top-1.5 w-4 h-4 rounded-full bg-accent-500 border-2 border-background-100"></span>
                    <h3 className="font-heading text-base font-semibold text-primary-600">{m.year}</h3>
                    <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{m.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-14 md:mt-20">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5 flex items-center gap-2">
              <i className="ri-shield-check-line text-primary-500"></i>
              版权与免责声明
            </h2>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8 space-y-4 text-sm text-foreground-600 leading-loose">
              <p>
                1. 本站为影视资讯展示与内容介绍类网站，仅提供影视作品的文字介绍、演员资料与图文信息，<strong className="text-foreground-900">不提供任何在线播放、下载或资源获取服务</strong>。
              </p>
              <p>
                2. 站内影视作品名称、剧情简介、演职人员等信息均来源于公开资料整理，部分内容为演示性质，仅供学习与交流，如与实际情况有出入，以官方发布为准。
              </p>
              <p>
                3. 站内所使用的图片素材均为演示用途的示意图片。如相关权利人对内容存在异议，请通过
                <Link href="/contact" to="/contact" className="text-primary-600 hover:text-primary-700 mx-1 underline-offset-2 hover:underline">联系我们</Link>
                页面提交说明，我们将及时核实并处理。
              </p>
              <p>
                4. 本站不接受任何形式的影视资源上传，也不对第三方链接的内容负责。用户在本站进行的收藏等操作仅保存在本地浏览器中，不会上传至服务器。
              </p>
            </div>
          </section>

          <div className="mt-12 md:mt-16 rounded-lg bg-primary-950 p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-5">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">有想法想告诉我们？</h2>
              <p className="mt-1.5 text-sm text-background-200/80">
                无论是内容建议、资料补充还是合作意向，都欢迎与我们联系。
              </p>
            </div>
            <Link
              href="/contact"
              to="/contact"
              className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-send-line"></i>
              前往联系我们
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}